import { Navigate, Outlet, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import SportsBioIcon from "./SportsBioIcon";

interface RequireAuthProps {
  role?: "player" | "scout" | "academy" | "parent";
  /** If true and user is logged in but onboarding for their role isn't complete, redirect. */
  requireOnboarding?: boolean;
}

const FullScreenLoader = () => (
  <div className="fixed inset-0 bg-background flex flex-col items-center justify-center gap-4">
    <SportsBioIcon size={56} />
    <div className="w-6 h-6 border-2 border-primary/30 border-t-primary rounded-full animate-spin" />
  </div>
);

const RequireAuth = ({ role, requireOnboarding }: RequireAuthProps) => {
  const { user, profile, academy, loading, sessionAuthenticated } = useAuth();
  const location = useLocation();

  if (loading) return <FullScreenLoader />;

  if (!user) {
    return <Navigate to="/auth" replace state={{ from: location.pathname }} />;
  }

  // WEB ONLY (sessionAuthenticated is hard-wired true on native — see
  // AuthContext). Persisted Supabase session but the operator hasn't
  // actively signed in within this tab session. Bounce them through
  // /auth so they re-authenticate; AuthScreen renders the form (it also
  // gates on sessionAuthenticated), and after a successful sign-in the
  // SIGNED_IN listener flips the flag and routes them back here.
  //
  // On a device this branch is never taken: a restored session is enough,
  // so force-killing the app and reopening lands on home, not login.
  if (!sessionAuthenticated) {
    return <Navigate to="/auth" replace state={{ from: location.pathname }} />;
  }

  // Profile may not have loaded yet (trigger lag) — let it through and let pages handle it.
  if (role && profile && profile.role && profile.role !== role) {
    // Wrong role — the user landed on a route for a persona that isn't
    // theirs (stale link, typo'd URL). Bounce to /auth; its already-
    // logged-in useEffect runs resolveDestinationForUser and lands them
    // at THEIR home/onboarding without showing any login UI or the
    // persona picker.
    return <Navigate to="/auth" replace />;
  }

  if (requireOnboarding && profile) {
    if (profile.role === "player" && !profile.onboarding_complete) {
      return <Navigate to="/player/onboarding" replace />;
    }
    if (profile.role === "scout" && !profile.onboarding_complete) {
      return <Navigate to="/scout/onboarding" replace />;
    }
    if (profile.role === "academy" && (!academy || !academy.onboarding_completed)) {
      // Academy onboarding state lives on the `academies` row, not on profiles.
      return <Navigate to="/academy/onboarding" replace />;
    }
    if (!profile.role) {
      // Ghost session — session valid but profile carries no role. See
      // SplashScreen.tsx header comment for full reasoning. Route to
      // /auth so resolveDestinationForUser can either recover via a
      // pending-signup blob or sign them out cleanly.
      return <Navigate to="/auth" replace />;
    }
  }

  return <Outlet />;
};

export default RequireAuth;
